import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
/* ── Constants ────────────────────────────────────────────────────────── */

const GRID_SIZE = 50;
const PADDING = 50;
const LEGEND_HEIGHT = 50;
const IDW_POWER = 2;
const WELL_RADIUS = 6;
const LABEL_FONT_SIZE = 8;

/* ── Helpers ──────────────────────────────────────────────────────────── */

function convertConc(val: number | null, unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

/** Inverse Distance Weighting interpolation */
function idwInterpolate(
  px: number, py: number,
  points: { x: number; y: number; value: number }[],
  power: number,
): number {
  let numerator = 0;
  let denominator = 0;
  for (const pt of points) {
    const dist = Math.sqrt((px - pt.x) ** 2 + (py - pt.y) ** 2);
    if (dist < 1e-10) return pt.value;
    const w = 1 / dist ** power;
    numerator += w * pt.value;
    denominator += w;
  }
  return denominator > 0 ? numerator / denominator : 0;
}

/** Blue -> Yellow -> Red color scale (0..1), returns [r,g,b] */
function heatColorRGB(t: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(1, t));
  if (clamped < 0.5) {
    const s = clamped / 0.5;
    return [Math.round(30 + s * 225), Math.round(80 + s * 175), Math.round(220 - s * 200)];
  }
  const s = (clamped - 0.5) / 0.5;
  return [255, Math.round(255 - s * 225), Math.round(20 + s * 10)];
}

function heatColor(t: number): string {
  const [r, g, b] = heatColorRGB(t);
  return `rgb(${r},${g},${b})`;
}

/* ── Compute well normalized positions preserving geographic aspect ratio */

interface WellPos { x: number; y: number }

function computeWellPositions(
  wells: AnalyticalTimeseriesResponse['wells'],
  canvasW: number,
  canvasH: number,
): WellPos[] {
  const mapW = canvasW - PADDING * 2;
  const mapH = canvasH - PADDING * 2 - LEGEND_HEIGHT;

  const withCoords = wells.filter(w => w.latitude != null && w.longitude != null);

  if (withCoords.length >= 2) {
    const validLats = withCoords.map(w => w.latitude!);
    const validLons = withCoords.map(w => w.longitude!);
    const defaultLat = validLats.reduce((a, b) => a + b, 0) / validLats.length;
    const defaultLon = validLons.reduce((a, b) => a + b, 0) / validLons.length;

    const resolvedLats = wells.map(w => w.latitude ?? defaultLat);
    const resolvedLons = wells.map(w => w.longitude ?? defaultLon);

    const minLat = Math.min(...resolvedLats);
    const maxLat = Math.max(...resolvedLats);
    const minLon = Math.min(...resolvedLons);
    const maxLon = Math.max(...resolvedLons);

    const latRange = maxLat - minLat || 0.0001;
    const lonRange = maxLon - minLon || 0.0001;

    // Convert to approximate feet to get true aspect ratio
    const midLat = (minLat + maxLat) / 2;
    const cosLat = Math.cos((midLat * Math.PI) / 180);
    const geoW = lonRange * cosLat; // proportional east-west extent
    const geoH = latRange;          // proportional north-south extent
    const geoAspect = geoW / geoH;
    const canvasAspect = mapW / mapH;

    // Fit inside canvas while preserving aspect ratio
    let scaleX: number, scaleY: number, offsetX = 0, offsetY = 0;
    if (geoAspect > canvasAspect) {
      // wider than canvas — fit to width, center vertically
      scaleX = mapW;
      scaleY = mapW / geoAspect;
      offsetY = (mapH - scaleY) / 2;
    } else {
      // taller — fit to height, center horizontally
      scaleY = mapH;
      scaleX = mapH * geoAspect;
      offsetX = (mapW - scaleX) / 2;
    }

    const MARGIN = 0.06; // inset from edges
    const usable = 1 - MARGIN * 2;

    return wells.map((w) => {
      const lat = w.latitude ?? defaultLat;
      const lon = w.longitude ?? defaultLon;
      const normX = MARGIN + ((lon - minLon) / lonRange) * usable;
      const normY = MARGIN + ((maxLat - lat) / latRange) * usable;
      return {
        x: PADDING + offsetX + normX * scaleX,
        y: PADDING + offsetY + normY * scaleY,
      };
    });
  }

  // Fallback: arrange in a grid
  if (wells.length === 0) return [];
  if (wells.length === 1) return [{ x: PADDING + mapW / 2, y: PADDING + mapH / 2 }];

  const cols = Math.ceil(Math.sqrt(wells.length));
  const rows = Math.ceil(wells.length / cols);
  return wells.map((_, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    return {
      x: PADDING + ((col + 0.5) / cols) * mapW,
      y: PADDING + ((row + 0.5) / rows) * mapH,
    };
  });
}

/* ── Label de-collision ─────────────────────────────────────────────── */

interface LabelInfo { x: number; y: number; text: string }

function decollidLabels(labels: LabelInfo[], minGap: number): LabelInfo[] {
  const result = labels.map(l => ({ ...l }));
  // Simple iterative push-apart
  for (let pass = 0; pass < 8; pass++) {
    for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const dx = result[j].x - result[i].x;
        const dy = result[j].y - result[i].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minGap && dist > 0) {
          const push = (minGap - dist) / 2;
          const nx = dx / dist;
          const ny = dy / dist;
          result[i].x -= nx * push;
          result[i].y -= ny * push;
          result[j].x += nx * push;
          result[j].y += ny * push;
        }
      }
    }
  }
  return result;
}

/* ── Read computed style color ──────────────────────────────────────── */

function getCSSColor(el: HTMLElement, prop: string, fallback: string): string {
  const val = getComputedStyle(el).getPropertyValue(prop).trim();
  return val || fallback;
}

/* ── Component ────────────────────────────────────────────────────────── */

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

interface HoverInfo {
  value: number;
  screenX: number;
  screenY: number;
  wellName?: string;
}

export function ConcentrationHeatmapMap({ data, unit }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Responsive canvas size — cap width to keep a readable aspect ratio
  const [containerWidth, setContainerWidth] = useState(900);
  const canvasWidth = Math.min(containerWidth, 800);
  const canvasHeight = Math.round(canvasWidth * 0.65);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 200) setContainerWidth(Math.round(w));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* ── Analyte selector ──────────────────────────────────────────────── */
  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound]) => ({ code, compound }));
  }, [data.wells]);

  const defaultAnalyte = useMemo(() => {
    const benzene = allAnalytes.find(a => a.compound.toLowerCase() === 'benzene');
    return benzene?.code ?? allAnalytes[0]?.code ?? '';
  }, [allAnalytes]);

  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(defaultAnalyte);
  const [hover, setHover] = useState<HoverInfo | null>(null);

  const hasRealCoords = useMemo(
    () => data.wells.filter(w => w.latitude != null && w.longitude != null).length >= 2,
    [data.wells],
  );

  /* ── Pixel positions for wells ──────────────────────────────────── */
  const pixelPositions = useMemo(
    () => computeWellPositions(data.wells, canvasWidth, canvasHeight),
    [data.wells, canvasWidth, canvasHeight],
  );

  /* ── Normalized positions (0..1) for IDW interpolation ─────────── */
  const mapLeft = PADDING;
  const mapTop = PADDING;
  const mapW = canvasWidth - PADDING * 2;
  const mapH = canvasHeight - PADDING * 2 - LEGEND_HEIGHT;

  const wellPoints = useMemo(() => {
    return data.wells.map((well, i) => {
      const analyteData = well.analytes[selectedAnalyte];
      const values = analyteData
        ? analyteData.measurements
            .map(m => convertConc(m.concentration, unit))
            .filter((v): v is number => v !== null)
        : [];
      const mean = values.length > 0
        ? values.reduce((s, v) => s + v, 0) / values.length
        : 0;

      const px = pixelPositions[i];
      return {
        name: well.well_name,
        px: px?.x ?? canvasWidth / 2,
        py: px?.y ?? canvasHeight / 2,
        // Normalized 0..1 for interpolation
        x: mapW > 0 ? ((px?.x ?? canvasWidth / 2) - mapLeft) / mapW : 0.5,
        y: mapH > 0 ? ((px?.y ?? canvasHeight / 2) - mapTop) / mapH : 0.5,
        value: mean,
        hasData: values.length > 0,
      };
    });
  }, [data.wells, selectedAnalyte, unit, pixelPositions, mapLeft, mapTop, mapW, mapH, canvasWidth, canvasHeight]);

  const activePoints = useMemo(
    () => wellPoints.filter(p => p.hasData),
    [wellPoints],
  );

  /* ── Interpolated grid ─────────────────────────────────────────── */
  const { grid, minVal, maxVal } = useMemo(() => {
    if (activePoints.length === 0) {
      return { grid: new Float64Array(0), minVal: 0, maxVal: 1 };
    }
    const g = new Float64Array(GRID_SIZE * GRID_SIZE);
    let mn = Infinity, mx = -Infinity;
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        const px = (col + 0.5) / GRID_SIZE;
        const py = (row + 0.5) / GRID_SIZE;
        const val = idwInterpolate(px, py, activePoints, IDW_POWER);
        g[row * GRID_SIZE + col] = val;
        if (val < mn) mn = val;
        if (val > mx) mx = val;
      }
    }
    return { grid: g, minVal: mn, maxVal: mx };
  }, [activePoints]);

  const range = maxVal - minVal || 1;
  const cellW = mapW / GRID_SIZE;
  const cellH = mapH / GRID_SIZE;

  /* ── De-collided labels ──────────────────────────────────────────── */
  const labelPositions = useMemo(() => {
    const raw: LabelInfo[] = wellPoints.map(w => ({
      x: w.px,
      y: w.py - WELL_RADIUS - 4,
      text: w.name,
    }));
    return decollidLabels(raw, LABEL_FONT_SIZE + 4);
  }, [wellPoints]);

  /* ── Draw on canvas ──────────────────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || grid.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Resolve CSS custom property colors from the DOM
    const textColor = getCSSColor(container, '--dv-text-primary', '#e2e8f0');
    const bgCardColor = getCSSColor(container, '--dv-bg-card', '#1a1a2e');
    const mutedColor = getCSSColor(container, '--dv-text-muted', '#64748b');
    const axisColor = getCSSColor(container, '--dv-chart-axis', '#94a3b8');

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw heatmap grid
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        const val = grid[row * GRID_SIZE + col];
        const t = (val - minVal) / range;
        const [r, g, b] = heatColorRGB(t);
        ctx.fillStyle = `rgba(${r},${g},${b},0.85)`;
        ctx.fillRect(mapLeft + col * cellW, mapTop + row * cellH, cellW + 0.5, cellH + 0.5);
      }
    }

    // Draw well markers
    for (const well of wellPoints) {
      ctx.beginPath();
      ctx.arc(well.px, well.py, WELL_RADIUS, 0, Math.PI * 2);
      if (well.hasData) {
        const [r, g, b] = heatColorRGB((well.value - minVal) / range);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
      } else {
        ctx.fillStyle = '#6b7280';
      }
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Draw de-collided labels
    ctx.font = `600 ${LABEL_FONT_SIZE}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    for (const label of labelPositions) {
      ctx.strokeStyle = bgCardColor;
      ctx.lineWidth = 3;
      ctx.strokeText(label.text, label.x, label.y);
      ctx.fillStyle = textColor;
      ctx.fillText(label.text, label.x, label.y);
    }

    // Legend
    const legendY = canvasHeight - LEGEND_HEIGHT;
    const legendW = mapW * 0.5;
    const legendX = mapLeft + (mapW - legendW) / 2;
    const legendBarH = 10;

    const gradient = ctx.createLinearGradient(legendX, 0, legendX + legendW, 0);
    gradient.addColorStop(0, heatColor(0));
    gradient.addColorStop(0.25, heatColor(0.25));
    gradient.addColorStop(0.5, heatColor(0.5));
    gradient.addColorStop(0.75, heatColor(0.75));
    gradient.addColorStop(1, heatColor(1));
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.roundRect(legendX, legendY, legendW, legendBarH, 3);
    ctx.fill();

    // Legend ticks
    ctx.font = '9px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = axisColor;
    for (let i = 0; i < 5; i++) {
      const t = i / 4;
      const val = minVal + t * range;
      const tx = legendX + t * legendW;
      const label = val < 0.01 && val > 0 ? val.toExponential(1) : val.toFixed(3);
      ctx.fillText(label, tx, legendY + legendBarH + 12);
    }

    // Legend title
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillStyle = mutedColor;
    ctx.fillText(`Concentration (${unit})`, canvasWidth / 2, legendY - 6);

  }, [grid, minVal, range, wellPoints, labelPositions, mapLeft, mapTop, mapW, mapH, cellW, cellH, unit, canvasWidth, canvasHeight]);

  /* ── Hover ─────────────────────────────────────────────────────────── */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || grid.length === 0) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvasWidth / rect.width;
      const scaleY = canvasHeight / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      // Check if hovering a well marker
      for (const well of wellPoints) {
        const dx = x - well.px;
        const dy = y - well.py;
        if (dx * dx + dy * dy <= (WELL_RADIUS + 4) ** 2) {
          setHover({
            value: well.value,
            screenX: e.clientX - rect.left,
            screenY: e.clientY - rect.top,
            wellName: well.name,
          });
          return;
        }
      }

      const col = Math.floor((x - mapLeft) / cellW);
      const row = Math.floor((y - mapTop) / cellH);
      if (col >= 0 && col < GRID_SIZE && row >= 0 && row < GRID_SIZE) {
        setHover({
          value: grid[row * GRID_SIZE + col],
          screenX: e.clientX - rect.left,
          screenY: e.clientY - rect.top,
        });
      } else {
        setHover(null);
      }
    },
    [grid, wellPoints, mapLeft, mapTop, cellW, cellH, canvasWidth, canvasHeight],
  );

  /* ── Empty state ───────────────────────────────────────────────────── */
  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  return (
      <div ref={containerRef}>
        {/* Analyte selector */}
        <div className="flex items-center gap-3 mb-4">
          <label className="text-xs font-medium" style={{ color: 'var(--dv-text-muted)' }}>
            Analyte
          </label>
          <select
            value={selectedAnalyte}
            onChange={e => setSelectedAnalyte(e.target.value)}
            className="text-xs rounded px-2 py-1 border"
            style={{
              background: 'var(--dv-bg-card)',
              borderColor: 'var(--dv-border-subtle)',
              color: 'var(--dv-text-primary)',
            }}
          >
            {allAnalytes.map(a => (
              <option key={a.code} value={a.code}>{a.compound}</option>
            ))}
          </select>
          {hasRealCoords && (
            <span className="text-[10px] px-2 py-0.5 rounded-full"
              style={{
                background: 'rgba(16, 185, 129, 0.1)',
                color: 'var(--dv-accent-emerald)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              GPS POSITIONED
            </span>
          )}
        </div>

        <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
          Concentration heatmap showing an interpolated color surface across the site.
          Warmer colors indicate higher contamination, helping visualize plume extent and hotspots.
        </p>

        {activePoints.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            NO ANALYTICAL DATA AVAILABLE
          </div>
        ) : (
          <div className="relative mx-auto" style={{ width: canvasWidth }}>
            <canvas
              ref={canvasRef}
              style={{ width: canvasWidth, height: canvasHeight, cursor: 'crosshair' }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHover(null)}
              role="img"
              aria-label="Concentration heatmap showing interpolated contamination"
            />

            {hover && (
              <div
                className="absolute pointer-events-none rounded-lg px-3 py-2 shadow-xl z-20"
                style={{
                  left: hover.screenX + 12,
                  top: hover.screenY - 36,
                  background: 'var(--dv-tooltip-bg)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid var(--dv-border-subtle)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                }}
              >
                {hover.wellName && (
                  <div className="text-[10px] font-semibold mb-0.5" style={{ color: 'var(--dv-text-secondary)' }}>
                    {hover.wellName}
                  </div>
                )}
                <div className="text-[11px] font-mono" style={{ color: 'var(--dv-text-primary)' }}>
                  {hover.value < 0.01 && hover.value > 0
                    ? hover.value.toExponential(2)
                    : hover.value.toFixed(4)}{' '}
                  {unit}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
  );
}